/**
 * 安全调用 analytics store（业务侧埋点入口，失败不阻断主流程）。
 */
import { useAnalyticsStore } from '@/stores/analytics';
import { getAcquisitionProps, withAcquisitionProps } from '@/utils/analytics-acquisition';

export { withAcquisitionProps };

function store() {
  return useAnalyticsStore();
}

/**
 * @param {{
 *   key: string,
 *   label?: string,
 *   value?: unknown,
 *   props?: unknown,
 *   funnelSessionId?: string,
 *   durationMs?: number,
 * }} entry
 * `value` 与 `props` 等价（推荐 props）；若含 funnelSessionId 会提升到 log 顶层。
 */
export function trackAnalyticsLog(entry) {
  try {
    store().trackLog(entry);
  } catch {
    /* ignore */
  }
}

/** @param {string} flowKey */
export function beginAnalyticsFlow(flowKey) {
  try {
    store().beginFlow(flowKey);
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} flowKey
 * @returns {number|undefined}
 */
export function endAnalyticsFlow(flowKey) {
  try {
    return store().endFlow(flowKey);
  } catch {
    return undefined;
  }
}

/**
 * @param {string} flowKey
 * @returns {number|undefined} 已持续毫秒（不结束 flow）
 */
export function peekAnalyticsFlowMs(flowKey) {
  try {
    return store().peekFlowMs(flowKey);
  } catch {
    return undefined;
  }
}

/** @param {string} [flowKey] @returns {string} */
export function ensureAnalyticsFunnelSession(flowKey) {
  try {
    return store().ensureFunnelSession(flowKey) || '';
  } catch {
    return '';
  }
}

/**
 * @param {string} sessionId
 * @param {string} [flowKey]
 * @returns {string}
 */
export function restoreAnalyticsFunnelSession(sessionId, flowKey) {
  try {
    return store().restoreFunnelSession(sessionId, flowKey) || '';
  } catch {
    return '';
  }
}

/** @param {string} [flowKey] @returns {string} */
export function endAnalyticsFunnelSession(flowKey) {
  try {
    return store().endFunnelSession(flowKey) || '';
  } catch {
    return '';
  }
}

/**
 * 标记本条属于漏斗：按 flowKey ensure session，并把 funnelSessionId 放进 props
 *（store.trackLog 会提升到 log 顶层，props 内不再保留）。
 * 同时附带 session 获客归因字段。
 * @param {Record<string, unknown>} [extra]
 * @param {string} [flowKey]
 */
export function withFunnelValue(extra = {}, flowKey) {
  const funnelSessionId = ensureAnalyticsFunnelSession(flowKey);
  return {
    ...getAcquisitionProps(),
    ...extra,
    funnelSessionId: funnelSessionId || undefined,
  };
}

/** @param {Record<string, unknown>} [extra] */
export function withDepositFunnelValue(extra = {}) {
  return withFunnelValue(extra, 'deposit');
}

/** @param {Record<string, unknown>} [extra] */
export function withTradeFunnelValue(extra = {}) {
  return withFunnelValue(extra, 'trade');
}

/** @param {boolean} cold */
export function setAnalyticsPageLoadCold(cold) {
  try {
    store().setPageLoadCold(cold);
  } catch {
    /* ignore */
  }
}

/** @param {number} durationMs */
export function noteAnalyticsPageLoadData(durationMs) {
  try {
    store().notePageLoadData(durationMs);
  } catch {
    /* ignore */
  }
}

/**
 * @param {number} durationMs
 * @param {string} [phase]
 */
export function noteAnalyticsPageLoadJs(durationMs, phase) {
  try {
    store().notePageLoadJs(durationMs, phase);
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ result?: string, rowCount?: number }} [info]
 */
export function markAnalyticsPageReady(info) {
  try {
    store().markPageReady(info);
  } catch {
    /* ignore */
  }
}
